import React, { useCallback, useMemo, useState } from 'react';
import CentralSwitchEmptyState from 'flow/components/central-switch/CentralSwitchEmptyState';
import CentralSwitchHeader from 'flow/components/central-switch/CentralSwitchHeader';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { ExtensionKey } from 'flow/types/EntityKeys';
import {
    selectContact,
    selectContactName,
} from 'flow/selectors/ExtensionSelectors';
import { useModal } from 'flow/contexts/ModalProvider';
import {
    loadExtensionsToCoListenTo,
    removeExtensionToCoListenTo,
} from './CoListeningAutoWidgetAlpha/CoListeningAlphaHelper';
import BaseListItem from 'flow/components/ui-components/BaseListItem';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import GenericSmallIconButton from 'flow/components/buttons/GenericSmallIconButton';

const ITEMS_MAX_COUNT = 10;

const CentralSwitchWidgetCoListeningAlpha = () => {
    const { i18next } = useAppContext();
    const { openModal } = useModal();

    const [extensionKeys, setExtensionKeys] = useState<ExtensionKey[]>(
        loadExtensionsToCoListenTo(),
    );

    const keysOfExtensionToShow = useMemo(
        () => extensionKeys.slice(0, ITEMS_MAX_COUNT),
        [extensionKeys],
    );

    const handleRemove = useCallback(
        (extensionKey: ExtensionKey) =>
            setExtensionKeys(removeExtensionToCoListenTo(extensionKey)),
        [],
    );

    const getContent = () => {
        if (!keysOfExtensionToShow.length) {
            return (
                <CentralSwitchEmptyState uniqueId={'CoListeningAutoAlpha'} />
            );
        }

        return (
            <ul className="px-4 pb-4">
                {keysOfExtensionToShow.map((extensionKey) => (
                    <ContactItem
                        key={`clwb-${extensionKey}`}
                        extensionKey={extensionKey}
                        onClickRemove={handleRemove}
                    />
                ))}
            </ul>
        );
    };

    const onCloseModal = useCallback(
        () => setExtensionKeys(loadExtensionsToCoListenTo()),
        [],
    );

    return (
        <>
            <CentralSwitchHeader>
                {i18next.t('Auto co-listening')}
                <GenericSmallIconButton
                    onClick={() =>
                        openModal('addExtensionsToAutoCoListenToModal', {
                            onClose: onCloseModal,
                        })
                    }
                    iconKey={'add'}
                    label={i18next.t('Add users to automatically listen to')}
                    className="bg-white text-gray-400"
                    size="small"
                />
            </CentralSwitchHeader>
            {getContent()}
            {extensionKeys.length > ITEMS_MAX_COUNT && (
                <div className="mb-4 h-6 px-4 text-end text-sm text-gray-400">{`+ ${
                    extensionKeys.length - ITEMS_MAX_COUNT
                } ${i18next.t('more')}`}</div>
            )}
        </>
    );
};

export default CentralSwitchWidgetCoListeningAlpha;

interface ContactItemProps {
    extensionKey: ExtensionKey;
    onClickRemove: (extensionKey: ExtensionKey) => void;
}

const ContactItem = ({ extensionKey, onClickRemove }: ContactItemProps) => {
    const { i18next } = useAppContext();
    const contact = useAppSelector((state) =>
        selectContact(state, extensionKey),
    );
    const memberName = useAppSelector((state) =>
        selectContactName(state, extensionKey),
    );

    return (
        <BaseListItem>
            <div className="flex w-full items-center justify-between">
                <div className="flex gap-x-3">
                    <ContactAvatar contact={contact} />
                    <div className="flex items-center overflow-hidden text-base/5 text-ellipsis text-gray-800">
                        {memberName || i18next.t('Contact.Unknown')}
                    </div>
                </div>
                <GenericSmallIconButton
                    onClick={() => onClickRemove(extensionKey)}
                    iconKey="delete"
                    size="small"
                    label={i18next.t('remove')}
                />
            </div>
        </BaseListItem>
    );
};

ContactItem.displayName = 'CSWCLAContactItem';
